import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize, Minimize, RefreshCw, ArrowLeft, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const IDLE_MS = 4000;

function readRefreshMinutes() {
  const raw = Number(new URLSearchParams(window.location.search).get('refresh'));
  if (!Number.isFinite(raw)) return 30;
  return Math.max(0, Math.min(24 * 60, raw));
}

/**
 * 대시보드 단독 화면(TV/모니터용). 사이드바 없이 전체 화면으로 띄우고,
 * 마우스가 멈추면 툴바와 커서를 숨긴다. ?refresh=분 (0이면 자동 새로고침 끔)
 */
const KioskDisplay = ({ children }) => {
  const { t: tr } = useTranslation();
  const T = (k, o) => tr(`kiosk.${k}`, o);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const [idle, setIdle] = useState(false);
  const [now, setNow] = useState(new Date());
  const [refreshMinutes] = useState(readRefreshMinutes);
  const idleTimer = useRef(null);
  const wakeLock = useRef(null);

  const wake = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), IDLE_MS);
  }, []);

  useEffect(() => {
    wake();
    window.addEventListener('mousemove', wake);
    window.addEventListener('keydown', wake);
    window.addEventListener('touchstart', wake);
    return () => {
      clearTimeout(idleTimer.current);
      window.removeEventListener('mousemove', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('touchstart', wake);
    };
  }, [wake]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!refreshMinutes) return undefined;
    const id = setTimeout(() => {
      if (navigator.onLine !== false) window.location.reload();
    }, refreshMinutes * 60 * 1000);
    return () => clearTimeout(id);
  }, [refreshMinutes]);

  useEffect(() => {
    const request = async () => {
      try {
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          wakeLock.current = await navigator.wakeLock.request('screen');
        }
      } catch (_) {
        /* not supported or denied */
      }
    };
    request();
    document.addEventListener('visibilitychange', request);
    return () => {
      document.removeEventListener('visibilitychange', request);
      wakeLock.current?.release?.().catch(() => {});
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch (_) {
      /* browser refused */
    }
  };

  const toolbarBtn = 'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold text-slate-200 hover:bg-white/10';

  return (
    <div className={`relative h-screen overflow-auto bg-[#050d1a] ${idle ? 'cursor-none' : ''}`}>
      {children}
      <div
        className={`fixed right-3 top-3 z-[1000] flex items-center gap-1 rounded-lg border border-white/10 bg-slate-900/85 px-1.5 py-1 shadow-lg backdrop-blur transition-opacity duration-300 ${idle ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      >
        <span className="flex items-center gap-1.5 px-2 text-xs tabular-nums text-slate-300">
          <Clock size={13} />
          {now.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
        {refreshMinutes ? (
          <span className="px-1 text-[11px] text-slate-500">{T('autoRefresh', { n: refreshMinutes })}</span>
        ) : null}
        <button type="button" onClick={() => window.location.reload()} className={toolbarBtn} title={T('refresh')}>
          <RefreshCw size={14} />
        </button>
        <button type="button" onClick={toggleFullscreen} className={toolbarBtn} title={isFullscreen ? T('exitFullscreen') : T('fullscreen')}>
          {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
          {isFullscreen ? T('exitFullscreen') : T('fullscreen')}
        </button>
        <a href="/" className={toolbarBtn} title={T('backTitle')}>
          <ArrowLeft size={14} /> {T('back')}
        </a>
      </div>
    </div>
  );
};

export default KioskDisplay;
